import { View, Input, Button, Textarea } from "@/components/ui";
import { InputFormControl } from "@/components/shared/input-form-control";
import { FormControlError } from "@/components/shared/form-control-error";
import { Spin } from "@/components/shared/spin";
import { PrimaryAddressCheckbox } from "../primary-address-checkbox";
import { useFormState, useFormContext } from "react-hook-form";
import { Typography } from "@/components/shared/typography";
import { useDeleteAddressStore } from "@/store/profile/deleteAddress";
import { useAddressByPostalCodeStore } from "@/store/auth/addressByPostalCode";
import { useParams } from "@/components/hooks/use-params";
import { useRoute } from "@/components/shared/router";
import { showToast } from "@/components/ui/toast/show-toast";
import { getErrorMessage } from "@/lib/helpers/string";
import { useApolloClient } from "@apollo/client";

interface UpdateAddressFormProps {
  onSubmit?: () => void;
  updateAddressLoading?: boolean;
  showTooltip: boolean;
}

export const UpdateAddressForm = ({
  onSubmit,
  updateAddressLoading,
  showTooltip,
}: UpdateAddressFormProps) => {
  const { id } = useParams<{ id: string }>();
  const { push } = useRoute();
  const { control, setValue } = useFormContext();
  const { errors } = useFormState({ control });
  const { fetch: deleteAddress, loading: deleteAddressLoading } =
    useDeleteAddressStore();
  const { loading: postalCodeLoading, fetch: addAddressByPostalCode } =
    useAddressByPostalCodeStore();
  const client = useApolloClient();
  const onDeleteAddress = async () => {
    try {
      if (!id) return;
      const res = await deleteAddress(
        {
          requestPayload: {
            input: {
              id: id,
            },
          },
        },
        { selfHandleError: true },
      );

      if (res.data?.result) {
        // Evict and refetch the addresses cache
        client.cache.evict({ fieldName: "clientAddresses" });
        client.cache.gc();
        push({
          pageKey: "addresses",
        });
      }

      showToast({
        title: "Success delete address",
        type: "success",
      });
      if (res.error) throw new Error(res.error);
    } catch (error) {
      const errMsg = getErrorMessage(error, "Error delete address");
      showToast({
        title: errMsg,
        type: "error",
      });
    }
  };

  return (
    <View className="p-4 md:p-0">
      <InputFormControl
        name="label"
        label="Label as"
        control={control}
        component={Input}
        componentProps={{
          placeholder: "Eg. Home, Office",
          maxLength: 30,
        }}
      />
      <InputFormControl
        name="postalCode"
        label="Postal Code"
        control={control}
        component={Input}
        componentProps={{
          placeholder: "Enter Postal Code",
          suffix: postalCodeLoading && <Spin />,
        }}
        changeFallBack={async (value) => {
          if (!value || value.length < 6 || value.length > 10) return;
          const result = await addAddressByPostalCode({
            requestPayload: {
              postalCode: value,
            },
          });

          if (result.data?.address) {
            setValue("fullAddress", result.data.address);
          }
        }}
      />
      <InputFormControl
        name="fullAddress"
        label="Address"
        className="mt-3"
        control={control}
        component={Textarea}
        componentProps={{
          placeholder: "Enter Address",
        }}
      />
      <Typography variant="body-md" className="mt-3">
        Unit Number
      </Typography>
      <View className="flex flex-row gap-3">
        <InputFormControl
          name="unitFloor"
          className="flex-1"
          control={control}
          component={Input}
          componentProps={{
            placeholder: "Floor",
          }}
          hideErrorMessage
        />
        <InputFormControl
          name="apartmentNumber"
          className="flex-1"
          control={control}
          component={Input}
          componentProps={{
            placeholder: "Apt No",
          }}
          hideErrorMessage
        />
      </View>
      {errors?.unitFloor?.type === "required" ||
      errors.apartmentNumber?.type === "required" ? (
        <FormControlError error="Floor and Apt No. required. Please indicate 0 if not applicable." />
      ) : (
        <Typography variant="body-sm" color="secondary-text" className="mt-1">
          If not applicable, enter 0 for Floor or Apt No.
        </Typography>
      )}

      <PrimaryAddressCheckbox showTooltip={showTooltip} />

      <View className="mt-3 flex flex-col gap-2 md:mt-1 md:flex-row md:items-end">
        <Button
          variant="secondary"
          color="danger"
          loading={deleteAddressLoading || updateAddressLoading}
          onClick={onDeleteAddress}
          className="md:ml-auto"
          iconName="trash"
          iconColor="danger"
          children="Delete Address"
        />
        <Button
          variant="primary"
          color="CTA"
          size="lg"
          loading={updateAddressLoading}
          onClick={onSubmit}
          className="native:hidden"
          children="Update"
        />
      </View>
    </View>
  );
};
